// Drill-down por indústria: Indústria → BU → sellers daquela indústria → pedidos → itens.
// "Seus produtos estão sofrendo corte no Seller X."
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, RefreshCw, X } from 'lucide-react';
import { getIndustriaResumo, IndustriaResumo } from '../api';
import { getPrefs } from '../auth';
import KpiCard from '../components/KpiCard';
import Dica from '../components/Dica';
import { AJUDA } from '../utils/ajudas';
import TrendChart from '../components/TrendChart';
import { KpisJanelas } from '../types';
import { fmtDataHoraFoto, fmtMoney, fmtNum, fmtPct, fmtPp } from '../utils/format';

const delta = (k: KpisJanelas): number | null =>
  (k.mtd.ns_qtd !== null && k.m1.ns_qtd !== null ? k.mtd.ns_qtd - k.m1.ns_qtd : null);

const deltaCls = (d: number | null) => (d === null ? '' : d > 0 ? 'delta-good' : d < 0 ? 'delta-bad' : '');

export default function Industria() {
  const { id } = useParams();
  const industriaId = Number(id);
  const [params, setParams] = useSearchParams();
  const buId = params.get('bu') ? Number(params.get('bu')) : null;
  const navigate = useNavigate();
  const voltar = () => {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate('/service-level/dashboard');
  };
  const prefs = getPrefs();
  const janelaRef = prefs.janela_ref || 'YTD';
  const customDe = prefs.custom_de;
  const customAte = prefs.custom_ate;

  const [data, setData] = useState<IndustriaResumo | null>(null);
  const [erro, setErro] = useState('');
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    let ativo = true;
    setData(null);
    setErro('');
    if (!Number.isInteger(industriaId) || industriaId <= 0) {
      setErro('Esta indústria não foi encontrada. Volte ao fechamento e selecione uma indústria.');
      return;
    }
    getIndustriaResumo(industriaId, janelaRef, { de: customDe, ate: customAte }, buId)
      .then((result) => { if (ativo) setData(result); })
      .catch((e: Error) => { if (ativo) setErro(e.message); });
    return () => { ativo = false; };
  }, [industriaId, buId, janelaRef, customDe, customAte, tentativa]);

  const selecionarBu = (bu: string | null) => {
    const next = new URLSearchParams(params);
    if (bu) next.set('bu', bu); else next.delete('bu');
    setParams(next);
  };

  if (erro) return <div className="card state-panel" role="alert"><h2>Não foi possível carregar a indústria</h2><p>{erro}</p><div className="filters"><button type="button" className="btn" onClick={() => setTentativa((n) => n + 1)}><RefreshCw size={16} /> Tentar novamente</button><Link className="btn secondary" to="/service-level/dashboard"><ArrowLeft size={16} /> Voltar ao fechamento</Link></div></div>;
  if (!data) return <div className="card state-panel" role="status" aria-live="polite"><p className="empty">Carregando indicadores da indústria…</p></div>;

  const k = data.kpis;
  const sellersOrdenados = [...data.sellers].sort((a, b) => (a.kpis.mtd.ns_qtd ?? 1) - (b.kpis.mtd.ns_qtd ?? 1));
  const busOrdenadas = [...data.por_bu].sort((a, b) => (a.kpis.mtd.ns_qtd ?? 1) - (b.kpis.mtd.ns_qtd ?? 1));

  return (
    <>
      <nav className="breadcrumb-nav" aria-label="Navegação estrutural">
        <Link to="/service-level/dashboard">Fechamento</Link><ChevronRight size={14} aria-hidden="true" /><span aria-current="page">Indústria{data.bu ? ' / BU' : ''}</span>
      </nav>
      <div className="page-header">
        <div>
          <button type="button" className="btn secondary mb-2" onClick={voltar}><ArrowLeft size={14} /> Voltar</button>
          <div className="eyebrow">Desempenho por indústria</div>
          <h1>{data.industria.nome}{data.bu ? ` · ${data.bu.sigla}` : ''}</h1>
          <div className="sub">
            {fmtNum(data.sellers.length)} sellers com volume neste recorte

          </div>
          {data.bu && <div className="mt-2"><span className="seller-chip">BU: {data.bu.sigla} · {data.bu.nome}<button type="button" onClick={() => selecionarBu(null)} aria-label="Limpar recorte de BU"><X size={14} /></button></span></div>}
        </div>
        <span className="foto-badge">Última atualização {fmtDataHoraFoto(data.ts_foto)}</span>
      </div>

      <div className="kpi-row">
        <KpiCard
          label="NS Quantidade (Fill Rate)" ajuda={AJUDA.ns}
          valores={{ hoje: k.hoje.ns_qtd, mtd: k.mtd.ns_qtd, m1: k.m1.ns_qtd, ref: k.ref.ns_qtd }}
          refLabel={k.ref_label} m1Label={k.m1_label} formato={fmtPct} formatoDelta={fmtPp} direcao="up"
          extra={`sobre pedidos já faturados (${fmtPct(k.mtd.pct_apurado)} do volume do mês)`}
        />
        <KpiCard
          label="Pedidos no Prazo" ajuda={AJUDA.prazo}
          valores={{ hoje: k.hoje.pct_no_prazo, mtd: k.mtd.pct_no_prazo, m1: k.m1.pct_no_prazo, ref: k.ref.pct_no_prazo }}
          refLabel={k.ref_label} m1Label={k.m1_label} formato={fmtPct} formatoDelta={fmtPp} direcao="up"
        />
        <KpiCard
          label="Corte" ajuda={AJUDA.corte}
          valores={{ hoje: k.hoje.pct_corte, mtd: k.mtd.pct_corte, m1: k.m1.pct_corte, ref: k.ref.pct_corte }}
          refLabel={k.ref_label} m1Label={k.m1_label} formato={fmtPct} formatoDelta={fmtPp} direcao="down"
          extra={`${fmtMoney(k.mtd.corte_valor)} cortados no mês`}
        />
        <KpiCard
          label="Faturamento do mês" ajuda={AJUDA.apuracao}
          valores={{ hoje: k.hoje.pct_apurado ?? null, mtd: k.mtd.pct_apurado ?? null, m1: k.m1.pct_apurado ?? null, ref: k.ref.pct_apurado ?? null }}
          refLabel={k.ref_label} m1Label={k.m1_label} formato={fmtPct} formatoDelta={fmtPp} direcao="up"
          extra={`${fmtNum(k.mtd.pedidos_em_aberto)} pedidos em aberto, aguardando faturamento`}
        />
      </div>

      <div className="card">
        <h2 className="flex items-start justify-between gap-2"><span>NS de quantidade — tendência diária do mês</span><Dica titulo="Tendência diária" texto={AJUDA.tendencia} /></h2>
        <TrendChart serie={data.serie_ns} />
      </div>

      {!data.bu && data.por_bu.length > 0 && (
        <div className="card">
          <div className="section-heading"><div><h2>Unidades de negócio</h2><p>Selecione uma BU para analisar seus produtos e sellers.</p></div><span className="badge perfil">{busOrdenadas.length} BUs</span></div>
          <div className="desktop-only table-wrap">
            <table>
              <thead>
                <tr>
                  <th>BU</th>
                  <th className="num">NS Qtd (MTD)</th>
                  <th className="num">Δ vs M-1</th>
                  <th className="num">No prazo (MTD)</th>
                  <th className="num">Corte (MTD)</th>
                </tr>
              </thead>
              <tbody>
                {busOrdenadas.map(({ bu, kpis }) => {
                    const d = delta(kpis);
                    return (
                      <tr key={bu.id}>
                        <td><button type="button" className="table-link" onClick={() => selecionarBu(String(bu.id))}>{bu.sigla}</button><div className="muted text-xs">{bu.nome}</div></td>
                        <td className="num"><strong>{fmtPct(kpis.mtd.ns_qtd)}</strong></td>
                        <td className={`num ${deltaCls(d)}`}>{fmtPp(d)}</td>
                        <td className="num">{fmtPct(kpis.mtd.pct_no_prazo)}</td>
                        <td className="num">{fmtMoney(kpis.mtd.corte_valor)}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          <div className="mobile-only">
            {busOrdenadas.map(({ bu, kpis }) => {
              const d = delta(kpis);
              return <button key={bu.id} type="button" className="ranking-card ranking-button" onClick={() => selecionarBu(String(bu.id))}>
                <div><div className="nome">{bu.sigla}</div><div className="sub">{bu.nome}</div><div className="sub">{fmtPct(kpis.mtd.pct_no_prazo)} no prazo · corte {fmtMoney(kpis.mtd.corte_valor)}</div></div>
                <div><div className="sub">NS Qtd · mês</div><div className="ns">{fmtPct(kpis.mtd.ns_qtd)}</div><div className={`sub text-right ${deltaCls(d)}`}>{fmtPp(d)} vs M-1</div></div>
              </button>;
            })}
          </div>
        </div>
      )}

      <div className="card">
        <div className="section-heading"><div><h2>Sellers neste recorte</h2><p>Ordenados pelo menor nível de serviço de quantidade no mês.</p></div><span className="badge perfil">{fmtNum(sellersOrdenados.length)} sellers</span></div>
        <div className="desktop-only table-wrap">
          <table>
            <thead>
              <tr>
                <th>Seller</th>
                <th className="num">NS Qtd (MTD)</th>
                <th className="num">Δ vs M-1</th>
                <th className="num">No prazo (MTD)</th>
                <th className="num">Corte (MTD)</th>
              </tr>
            </thead>
            <tbody>
              {sellersOrdenados.map(({ seller, kpis }) => {
                const d = delta(kpis);
                return (
                  <tr key={seller.seller_id}>
                    <td>
                      <Link to={`/service-level/sellers/${seller.seller_id}?industria=${industriaId}`}><strong>{seller.nome_fantasia}</strong></Link>
                      <div className="muted text-[11px]">{seller.uf_base}</div>
                    </td>
                    <td className="num"><strong>{fmtPct(kpis.mtd.ns_qtd)}</strong></td>
                    <td className={`num ${deltaCls(d)}`}>{fmtPp(d)}</td>
                    <td className="num">{fmtPct(kpis.mtd.pct_no_prazo)}</td>
                    <td className="num">{fmtMoney(kpis.mtd.corte_valor)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mobile-only">
          {sellersOrdenados.map(({ seller, kpis }) => {
            const d = delta(kpis);
            return (
              <Link key={seller.seller_id} to={`/service-level/sellers/${seller.seller_id}?industria=${industriaId}`} className="ranking-card">
                <div>
                  <div className="nome">{seller.nome_fantasia}</div>
                  <div className="sub">{seller.uf_base} · {fmtPct(kpis.mtd.pct_no_prazo)} no prazo</div>
                  <div className="sub">Corte no mês: {fmtMoney(kpis.mtd.corte_valor)}</div>
                </div>
                <div>
                  <div className="sub text-right">NS Qtd · mês</div>
                  <div className="ns">{fmtPct(kpis.mtd.ns_qtd)}</div>
                  <div className={`sub text-right ${deltaCls(d)}`}>{fmtPp(d)} vs M-1</div>
                </div>
              </Link>
            );
          })}
        </div>
        {sellersOrdenados.length === 0 && <p className="empty">Nenhum seller com volume neste recorte.</p>}
        <p className="chart-note">Os KPIs de cada seller consideram apenas os produtos {data.bu ? `da BU ${data.bu.sigla}` : `de ${data.industria.nome}`}.</p>
      </div>
    </>
  );
}
